import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import type { SearchResults } from './api';
import { usePlainLanguage } from './plainLanguage';
import { Empty, ErrorBanner, Modal, StatusPill, describe } from './ui';

/** Below this a query matches most of the table, which is a round trip with no answer. */
const MIN_QUERY = 2;

/** Long enough to skip the letters someone types on the way to a word. */
const DEBOUNCE_MS = 180;

const GROUP = 'mt-4 mb-1 text-[11px] font-semibold tracking-[0.06em] text-muted uppercase first:mt-0';
const HIT = 'flex items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-text no-underline hover:bg-hover';

/**
 * Search over organizations and outlets.
 *
 * A dialog rather than a field in the bar with a list hanging under it: the results
 * need room, the input wants the whole screen's attention on a phone, and the native
 * <dialog> already handles Escape, focus and the click outside.
 */
export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  // A merchant has one organization, so finding it again tells them nothing, and each of
  // its shops belongs to the same business: only the shop names are worth showing.
  const plain = usePlainLanguage();

  const inputRef = useRef<HTMLInputElement>(null);
  // Only the newest query may write to state, or a slow request for "ih" lands after
  // the quick one for "ihsaan" and puts the wider results back on screen.
  const latest = useRef(0);

  // Each opening starts empty: the last search answered a question already asked.
  useEffect(() => {
    if (!open) return;
    setTerm('');
    setResults(null);
    setError(null);
    setSearching(false);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const query = term.trim();
    if (query.length < MIN_QUERY) {
      setResults(null);
      setSearching(false);
      return;
    }

    const ticket = ++latest.current;
    setSearching(true);

    const timer = setTimeout(() => {
      api
        .search(query)
        .then((found) => {
          if (ticket !== latest.current) return;
          setResults(found);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (ticket === latest.current) setError(describe(cause));
        })
        .finally(() => {
          if (ticket === latest.current) setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term]);

  const query = term.trim();
  const organizations = plain ? [] : (results?.organizations ?? []);
  const hits = results ? organizations.length + results.outlets.length : 0;

  return (
    <Modal open={open} title="Search" onClose={onClose}>
      <input
        ref={inputRef}
        type="search"
        value={term}
        autoComplete="off"
        placeholder={plain ? 'A shop…' : 'An organization or an outlet…'}
        onChange={(event) => setTerm(event.target.value)}
      />

      <ErrorBanner message={error} />

      {query.length < MIN_QUERY && (
        <Empty>
          {query.length > 0
            ? 'Keep typing…'
            : plain
              ? 'Search your shops by name.'
              : 'Search organizations and outlets by name.'}
        </Empty>
      )}

      {query.length >= MIN_QUERY && searching && !results && <Empty>Searching…</Empty>}

      {query.length >= MIN_QUERY && results && hits === 0 && !searching && (
        <Empty>Nothing matches “{query}”.</Empty>
      )}

      {results && hits > 0 && (
        <div className="mt-3.5 max-h-[48vh] overflow-y-auto">
          {organizations.length > 0 && (
            <>
              <div className={GROUP}>Organizations</div>
              {organizations.map((org) => (
                <Link
                  key={org.id}
                  className={HIT}
                  to={`/organizations/${org.id}/outlets`}
                  onClick={onClose}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">
                      <Highlight text={org.name} term={query} />
                    </span>
                    <span className="block font-mono text-xs text-muted">
                      <Highlight text={org.slug} term={query} />
                    </span>
                  </span>
                  <StatusPill status={org.status} />
                </Link>
              ))}
            </>
          )}

          {results.outlets.length > 0 && (
            <>
              <div className={GROUP}>{plain ? 'Shops' : 'Outlets'}</div>
              {results.outlets.map((outlet) => (
                <Link
                  key={outlet.id}
                  className={HIT}
                  to={`/organizations/${outlet.org.id}/outlets/${outlet.id}`}
                  onClick={onClose}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">
                      <Highlight text={outlet.name} term={query} />
                    </span>
                    {/* Which customer it belongs to is the thing that tells two
                        outlets called "Main Shop" apart. */}
                    {!plain && (
                      <span className="block text-[12.5px] text-muted">
                        {outlet.org.name}
                        {outlet.mposShopId && (
                          <>
                            {' · '}
                            <Highlight text={outlet.mposShopId} term={query} />
                          </>
                        )}
                      </span>
                    )}
                  </span>
                  <StatusPill status={outlet.status} />
                </Link>
              ))}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Marks the part that matched, so a hit on an MPOS id rather than a name does not look
 * like a result that arrived for no reason. First occurrence only: this is an
 * explanation, not a find-in-page.
 */
function Highlight({ text, term }: { text: string; term: string }) {
  const at = text.toLowerCase().indexOf(term.toLowerCase());
  if (at < 0) return <>{text}</>;
  const end = at + term.length;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[3px] bg-accent/30 text-inherit">{text.slice(at, end)}</mark>
      {text.slice(end)}
    </>
  );
}
